/**
 * Created by Ning Jiang on 5/24/2018.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {IReelStopSpinData} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {ISymbolStateConfig, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {SpinReelController} from "@nolimitcity/slot-game/bin/game/reel/spinreel/SpinReelController";
import {FruitsGameMode} from "../../gamemode/FruitsGameMode";
import {FruitsReelSymbol} from "../../reelsymbol/FruitsReelSymbol";
import {FruitsParsedGameData} from "../../server/data/FruitsParsedGameData";
import {FruitsParsedInitData} from "../../server/data/FruitsParsedInitData";
import {IFruitsParsedServerData} from "../../server/data/FruitsParsedServerData";
import {FruitsHelper} from "../../utils/FruitsHelper";
import {FruitsReels} from "../reelarea/FruitsReels";
import {FruitsReelPartAnimator} from "./FruitsReelPartAnimator";

export class FruitsReelController extends SpinReelController {

    private readonly _reelStopDelayBonus:number[] = [0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2];
    private readonly _reelFastSpinDelayBonus:number[] = [0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05];
    private readonly _reelQuickStopDelayBonus:number[] = [0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1];
    private readonly _reelFastSpinDelayRespin:number[] = [0, 0, 0, 0, 0, 0, 2, 0, 0, .5, 0, 0, .5, 0, 0, .5];

    private _gameData:FruitsParsedGameData;
    private _scatterLevel:number = 1;
    public static readonly UPDATE_SCATTER_LEVEL:string = "fruitsReelController_updateScatterLevel";
    private _scatterLevelSymbol:string = "S";
    private _realNextReelSetName:string;
    private _isNextSticky:boolean;
    private _isStickyMode:boolean;

    private _stopIndex:number = -1;
    set stopIndex(value:number) {
        this._stopIndex = value;
    }

    constructor(reelId:number) {
        super(reelId);
    }

    protected addEventListeners():void {
        super.addEventListeners();
        EventHandler.addEventListener(this, FruitsReelController.UPDATE_SCATTER_LEVEL, (event:GameEvent) => this.updateScatterLevel(event.params[0]));
        EventHandler.addEventListener(this, FruitsReels.SET_FAKE_REELSET, (event:GameEvent) => this.onSetFakeReelset());
    }

    private onSetFakeReelset():void {
        this.fakeNextReelSetNameWhenTransition(this._gameData)
    }

    protected parseInitData(data:FruitsParsedInitData):void {
        super.parseInitData(data);
        this.fakeNextReelSetNameWhenTransition(data);
    }

    public onGameDataParsed(data:FruitsParsedGameData):void {
        this._gameData = data;
        super.onGameDataParsed(data);
        this._isNextSticky = data.nextMode === FruitsGameMode.STICKY_RESPIN;
        this._isStickyMode = data.mode === FruitsGameMode.STICKY_RESPIN;

        if(!(this._gameMode === FruitsGameMode.BONUS_GAME && data.numberOfSpinsRemaining === 0)) {
            this.fakeNextReelSetNameWhenTransition(data);
        }
        // Copied this line from old FruitsReelAreaController.onStopSpin.
        if(this._gameData.mode === "BONUS_GAME" && this._gameData.nextMode === "NORMAL") {
            this._nextFullReelSet = Helper.getReelSet("BONUS_GAME_REELSET", this._reelId);
        }
    }

    // Trick the next reel set so we don't need to change the already added top symbol and symbolToBeAdded when changing reelId.
    private fakeNextReelSetNameWhenTransition(data:IFruitsParsedServerData):void {
        this._realNextReelSetName = data.nextReelSetName;
        if(data.isBonusTriggeredThisSpin && data.nextReelSetName === "BONUS_GAME_REELSET") {
            // This fake reelSet is the same with "BONUS_GAME_REELSET" but in normal game mode order.
            this._nextFullReelSet = Helper.getReelSet("TRANSITION_TEMP_BONUS_GAME_REELSET", this._reelId);
        } else if(data.nextReelSetName === "BASE_REELSET") {
            EventHandler.dispatchEvent(new GameEvent(FruitsReelPartAnimator.REPLACE_X, true));
            this._nextFullReelSet = Helper.getReelSet("BASE_REELSET", this._reelId);
        }
    }

    public changeReelId(newId:number):void {
        this._reelId = newId;
    }

    public changeAllReelSets():void {
        this._nextFullReelSet = Helper.getReelSet(this._realNextReelSetName, this._reelId);
        this.setCurrentReelSet({reelSetName : this._realNextReelSetName});
    }

    public getSpinStopOffset():number {
        return 0;
    }

    protected isSpecialSymbol(symbolName:string):boolean {
        return super.isSpecialSymbol(symbolName);
    }

    protected addNewSymbol(symName:string, state?:ISymbolStateConfig):FruitsReelSymbol {
        const sym:StateReelSymbol = this._reelPartCreator.createNewSymbol(symName, this._reelId, state);
        if(sym.symName.substr(0, 1) === "S" && this._scatterLevelSymbol !== sym.symName)
            symName = this._scatterLevelSymbol;

        return <FruitsReelSymbol>super.addNewSymbol(symName, state);
    }

    private updateScatterLevel(level:number):void {
        if(this._scatterLevel !== level) {
            switch(level) {
                case 1:
                    this._scatterLevelSymbol = "S";
                    break;
                case 2:
                    this._scatterLevelSymbol = "S_B";
                    break;
                case 3:
                    this._scatterLevelSymbol = "S_C";
                    break;
                case 4:
                    this._scatterLevelSymbol = "S_D";
                    break;
                default:
                    break;
            }
        }

        this._scatterLevel = level;
    }

    protected getStopDelayDelta(stopSpinData:IReelStopSpinData):number {
        let delta:number = 0;

        // This line copies from FruitsReelAreaControler.stopSpin().
        if(this._stopIndex === 3 && this.checkNearSticky()) {
            delta += 1.2;
        }

        if(this._stopIndex === 4 && this._isNextSticky && !this._isStickyMode) {
            delta += 1.6
        }

        if(this._gameData!.randomSymbolOverlayTriggered) {
            if(this._stopIndex === 0) {
                delta += 1.5;
            }
        }

        if(this._gameMode === FruitsGameMode.BONUS_GAME) {
            return this.getBonusStopDelay(this.reelId, this._stopIndex);

        }

        if(this._reelPartAnimator.isQuickStop) {
            return this.getQuickStopDelay(this.reelId) + delta;
        }

        if(StageManager.orientation === Orientation.LANDSCAPE ? this.hasNearWin(this._stopIndex) : false) {
            delta += StageManager.orientation === Orientation.LANDSCAPE ? this.getNearWinStopDelay(this._stopIndex) : 0;
        }

        if(this._reelPartAnimator.isFastSpin) {
            return (this.getFastSpinStopDelay(this.reelId) + delta);
        }

        return this.getNormalStopDelay(this._stopIndex) + delta;
    }

    private getBonusStopDelay(reelId:number, index:number):number {
        let delta:number = 0;

        if(index < 12 && this._gameData.collectedScatterBefore === 12) {
            return 0;
        }

        if(index === 12) {
            if(this._reelPartAnimator.isFastSpin) {
                delta += 1;
            } else {
                delta += 2;
            }
        }

        if(this._reelPartAnimator.isFastSpin) {
            return this._reelFastSpinDelayBonus[reelId] + delta;
        }

        return this._reelStopDelayBonus[reelId] + delta;
    }

    protected getQuickStopDelay(reelId:number):number {
        return this._gameMode === FruitsGameMode.BONUS_GAME ? this._reelQuickStopDelayBonus[reelId] : super.getQuickStopDelay(reelId);
    }

    protected getFastSpinStopDelay(reelId:number):number {
        return StageManager.orientation === Orientation.PORTRAIT &&
        this._gameData! && this.checkReelAttention(reelId > 3 ? reelId === 6 ? reelId - 4 : reelId - 2 : 0) ?
            this._reelFastSpinDelayRespin[reelId] : this.getQuickStopDelay(reelId);
    }

    private checkReelAttention(reelId:number):boolean {
        let reelPos:[number, number] = FruitsHelper.translateReelIndex(reelId);
        return FruitsReels.nearWinPositions[reelPos[0]][reelPos[1]] || FruitsReels.bigWinExcitementPositions[reelPos[0]][reelPos[1]];
    }

    private checkNearSticky():boolean {
        return FruitsReels.nearWinPositions[1][2] || FruitsReels.nearWinPositions[1][1];
    }

    public hasNearWin(reelId?:number):boolean {
        reelId = reelId ?? this.reelId;
        return (FruitsReels.nearWinReels != null && FruitsReels.nearWinReels[reelId]) || (this._nearWinBigWinReels != null && this._nearWinBigWinReels[reelId]);
    }
}